import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "modal", "headerIcon", "headerTitle", "headerSubtitle", 
    "overallProgress", "uploadsList", "successScreen", 
    "errorScreen", "errorMessage"
  ]
  
  connect() {
    this.uploadFiles = []
    this.completedUploads = 0
    this.failedUploads = 0
    this.compressionTracking = new Map() // Track compression progress
    this.completionProcessed = new Set() // Prevent duplicate completions
    this.simulatedProgress = new Map() // Track simulated progress for each file
    this.progressIntervals = new Map() // Store intervals for cleanup
  }

  // Called by track_upload_controller to initialize upload tracking
  startUploadTracking(files) {
    this.uploadFiles = files
    this.completedUploads = 0
    this.failedUploads = 0
    this.completionProcessed = new Set() // Reset completion tracking
    
    // Update header
    this.headerTitleTarget.textContent = `Upload de ${files.length} fichier(s) en cours...`
    this.overallProgressTarget.textContent = `0/${files.length}`
    
    // Clear previous uploads list
    this.uploadsListTarget.innerHTML = ''
    
    // Create UI for each file
    files.forEach((file, index) => {
      this.createFileProgressUI(file, index)
    })
    
    // Show modal
    this.show()
  }
  
  // New unified tracking method for upload + compression
  startUnifiedTracking(files) {
    
    // Prevent duplicate modal initialization
    if (this.isVisible()) {
      console.warn('⚠️ Modal already visible, preventing duplicate initialization')
      return
    }
    
    this.uploadFiles = files
    this.completedUploads = 0
    this.failedUploads = 0
    this.compressionTracking.clear()
    this.completionProcessed = new Set() // Reset completion tracking
    
    // Update header for upload and optimization
    this.headerTitleTarget.textContent = `Upload et optimisation de ${files.length} stem(s)...`
    this.overallProgressTarget.textContent = `0/${files.length}`
    
    // Clear previous uploads list
    this.uploadsListTarget.innerHTML = ''
    
    // Create UI for each file with stem info
    files.forEach((file, index) => {
      this.createUnifiedProgressUI(file, index)
      this.compressionTracking.set(file.id, { phase: 'upload', progress: 0 })
      // Start simulated progress immediately
      this.startSimulatedProgress(file.id, file.size || 0)
    })
    
    // Show modal
    this.show()
  }
  
  createUnifiedProgressUI(file, index) {
    const fileItem = document.createElement('div')
    fileItem.className = 'file-progress-item mb-4 last:mb-0 transform transition-all duration-300 hover:scale-[1.02]'
    fileItem.style = `animation: fadeInUp 0.3s ease-out ${(index + 1) * 0.1}s both;`
    fileItem.dataset.fileId = file.id
    
    // Start simulated progress immediately
    this.startSimulatedProgress(file.id, file.size)
    
    fileItem.innerHTML = `
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-2 flex-1">
          <!-- Status icon -->
          <div class="file-status-icon" data-status="uploading" data-status-icon="${file.id}">
            <i class="fa-solid fa-cloud-arrow-up text-primary"></i>
          </div>
          
          <!-- File/Stem name -->
          <div class="flex items-center gap-2 flex-1">
            <i class="fa-solid fa-music text-content text-sm"></i>
            <span class="text-sm font-medium text-contrast truncate" title="${file.stemName || file.name}">
              ${file.stemName || file.name}
            </span>
          </div>
          
          <!-- Progress indicator -->
          <div class="flex items-center gap-2">
            <span class="text-sm font-mono text-content" data-progress-text="${file.id}">0%</span>
          </div>
        </div>
      </div>
      
      <!-- Progress bar simple -->
      <div class="relative h-2 bg-contour bg-opacity-40 rounded-full overflow-hidden" data-progress-container="${file.id}">
        <!-- Progress fill -->
        <div class="h-full rounded-full transition-all duration-500 ease-out"
             data-progress-bar="${file.id}"
             style="width: 0%; background: linear-gradient(to right, #3b82f6, #2563eb);">
        </div>
      </div>
      
      <!-- Status info simple -->
      <div class="text-xs text-content mt-1 opacity-70">
        <span data-status-text="${file.id}">Préparation du fichier...</span>
      </div>
    `
    
    this.uploadsListTarget.appendChild(fileItem)
  }

  createFileProgressUI(file, index) {
    const fileItem = document.createElement('div')
    fileItem.className = 'file-progress-item mb-4 last:mb-0 transform transition-all duration-300 hover:scale-[1.02]'
    fileItem.style = `animation: fadeInUp 0.3s ease-out ${(index + 1) * 0.1}s both;`
    fileItem.dataset.fileId = file.id
    
    fileItem.innerHTML = `
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-2 flex-1">
          <!-- Status icon -->
          <div class="file-status-icon" data-status="uploading">
            <i class="fa-solid fa-spinner fa-spin text-primary"></i>
          </div>
          
          <!-- File name -->
          <div class="flex items-center gap-2 flex-1">
            <i class="fa-solid fa-music text-content text-sm"></i>
            <span class="text-sm font-medium text-contrast truncate" title="${file.name}">
              ${file.name}
            </span>
          </div>
          
          <!-- Progress percentage -->
          <div class="flex items-center gap-1">
            <span class="text-sm font-mono text-content" data-progress-text="${file.id}">0%</span>
            <span class="text-sm" data-progress-emoji="${file.id}">⏳</span>
          </div>
        </div>
      </div>
      
      <!-- Progress bar -->
      <div class="relative h-2 bg-contour bg-opacity-40 rounded-full overflow-hidden">
        <!-- Background shimmer effect -->
        <div class="absolute inset-0 opacity-20 bg-gradient-to-r from-transparent via-white to-transparent"
             style="animation: shimmer 2s infinite; background-size: 200% 100%;"></div>
        
        <!-- Progress fill -->
        <div class="h-full rounded-full transition-all duration-300 ease-out relative bg-gradient-to-r from-primary to-secondary"
             data-progress-bar="${file.id}"
             style="width: 0%;">
          <!-- Sparkle effect -->
          <div class="absolute right-0 top-0 h-full w-4 bg-gradient-to-r from-transparent to-white opacity-60"
               style="animation: sparkle 1s infinite;"></div>
        </div>
      </div>
      
      <!-- Size info -->
      <div class="text-xs text-content mt-1 opacity-60">
        <span data-uploaded-size="${file.id}">0</span> / ${this.formatFileSize(file.size)}
      </div>
    `
    
    this.uploadsListTarget.appendChild(fileItem)
  }

  updateFileProgress(fileId, progress, uploadedBytes = null) {
    const progressBar = document.querySelector(`[data-progress-bar="${fileId}"]`)
    const progressText = document.querySelector(`[data-progress-text="${fileId}"]`)
    const progressEmoji = document.querySelector(`[data-progress-emoji="${fileId}"]`)
    const uploadedSize = document.querySelector(`[data-uploaded-size="${fileId}"]`)
    
    if (progressBar) {
      progressBar.style.width = `${progress}%`
    }
    
    if (progressText) {
      progressText.textContent = `${progress}%`
    }
    
    if (progressEmoji) {
      progressEmoji.textContent = this.getProgressEmoji(progress)
    }
    
    if (uploadedSize && uploadedBytes !== null) {
      uploadedSize.textContent = this.formatFileSize(uploadedBytes)
    }
  }

  markFileComplete(fileId, success = true) {
    // Prevent duplicate completion processing
    if (this.completionProcessed && this.completionProcessed.has(fileId)) {
      return
    }
    
    const fileItem = document.querySelector(`[data-file-id="${fileId}"]`)
    if (!fileItem) return
    
    // Mark as processed
    if (this.completionProcessed) {
      this.completionProcessed.add(fileId)
    }
    
    const statusIcon = fileItem.querySelector('.file-status-icon')
    const progressEmoji = document.querySelector(`[data-progress-emoji="${fileId}"]`)
    
    if (success) {
      this.completedUploads++
      if (statusIcon) {
        statusIcon.innerHTML = '<i class="fa-solid fa-check-circle text-green-500"></i>'
      }
      if (progressEmoji) {
        progressEmoji.textContent = '✅'
      }
      this.updateFileProgress(fileId, 100)
    } else {
      this.failedUploads++
      if (statusIcon) {
        statusIcon.innerHTML = '<i class="fa-solid fa-times-circle text-red-500"></i>'
      }
      if (progressEmoji) {
        progressEmoji.textContent = '❌'
      }
    }
    
    // Update overall progress
    const totalFiles = this.uploadFiles.length
    const completedCount = this.completedUploads + this.failedUploads
    this.overallProgressTarget.textContent = `${completedCount}/${totalFiles}`
    
    // Check if all uploads are complete
    if (completedCount === totalFiles) {
      this.handleAllUploadsComplete()
    }
  }

  handleAllUploadsComplete() {
    if (this.failedUploads === 0) {
      // All successful - show success screen
      this.headerTitleTarget.textContent = 'Upload terminé !'
      this.headerSubtitleTarget.textContent = 'Tous les fichiers ont été uploadés avec succès'
      
      // Show success screen briefly before closing
      setTimeout(() => {
        this.successScreenTarget.classList.remove('hidden')
        
        // Auto-hide after 2 seconds
        setTimeout(() => {
          this.hide()
        }, 2000)
      }, 500)
    } else {
      // Some failures - show error
      this.headerTitleTarget.textContent = 'Upload terminé avec des erreurs'
      this.headerSubtitleTarget.textContent = `${this.failedUploads} fichier(s) ont échoué`
      
      this.errorScreenTarget.classList.remove('hidden')
      this.errorMessageTarget.textContent = `${this.failedUploads} fichier(s) n'ont pas pu être uploadés`
    }
  }

  getProgressEmoji(progress) {
    if (progress >= 100) return '✅'
    if (progress >= 75) return '📦'
    if (progress >= 50) return '⚡'
    if (progress >= 25) return '🔄'
    return '⏳'
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  show() {
    const modal = document.getElementById('upload-progress-modal')
    if (modal) {
      modal.classList.remove('hidden')
    } else {
      console.error('❌ Modal not found!')
    }
  }

  hide() {
    const modal = document.getElementById('upload-progress-modal')
    if (modal) {
      modal.classList.add('hidden')
      
      // Clear all simulation intervals
      this.progressIntervals.forEach(interval => clearInterval(interval))
      this.progressIntervals.clear()
      
      // Clear all state to prevent any lingering references
      this.uploadFiles = []
      this.completedUploads = 0
      this.failedUploads = 0
      this.compressionTracking.clear()
      this.completionProcessed.clear()
      this.simulatedProgress.clear()
    }
  }

  // Allow external controllers to check if modal is visible
  isVisible() {
    const modal = document.getElementById('upload-progress-modal')
    return modal && !modal.classList.contains('hidden')
  }
  
  calculateEstimatedTime(fileSize) {
    const fileSizeMB = fileSize / (1024 * 1024)
    return Math.min(Math.max(fileSizeMB * 1500, 2000), 12000) // 1.5s per MB, min 2s, max 12s
  }
  
  formatEstimatedTime(timeMs) {
    const seconds = Math.ceil(timeMs / 1000)
    if (seconds < 60) {
      return `${seconds}s`
    }
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }
  
  updateTimeRemaining(fileId, remainingTimeMs) {
    const timeRemainingElement = document.querySelector(`[data-time-remaining="${fileId}"]`)
    if (timeRemainingElement && remainingTimeMs > 0) {
      const seconds = Math.ceil(remainingTimeMs / 1000)
      timeRemainingElement.textContent = `${seconds}s restantes`
    } else if (timeRemainingElement) {
      timeRemainingElement.textContent = 'Finalisation...'
    }
  }
  
  startSimulatedProgress(fileId, fileSize) {
    // Clear any existing simulation for this file
    this.stopSimulatedProgress(fileId)
    
    const baseDuration = this.calculateEstimatedTime(fileSize)
    const steps = 200 // Even more granular for ultra-smooth animation
    const intervalDuration = baseDuration / steps
    const startTime = Date.now()
    
    let currentProgress = 0
    let lastUpdateTime = Date.now()
    this.simulatedProgress.set(fileId, currentProgress)
    
    // Add some realistic network variation
    let speedMultiplier = 1
    let speedVariationCounter = 0
    
    const interval = setInterval(() => {
      currentProgress = this.simulatedProgress.get(fileId) || 0
      const elapsedTime = Date.now() - startTime
      const remainingTime = Math.max(0, baseDuration - elapsedTime)
      
      // Simulate network speed variations every ~2 seconds
      speedVariationCounter++
      if (speedVariationCounter > 20) {
        speedMultiplier = 0.7 + Math.random() * 0.6 // Speed varies between 70% and 130%
        speedVariationCounter = 0
      }
      
      // Ultra-realistic upload curve with micro-variations
      let increment
      const microVariation = 0.95 + Math.random() * 0.1 // 95-105% micro variation
      
      if (currentProgress < 10) {
        // Initial connection and handshake - slower start
        increment = (0.8 + Math.random() * 1.2) * speedMultiplier * microVariation
      } else if (currentProgress < 25) {
        // Ramp up phase - accelerating
        increment = (1.2 + Math.random() * 1.8) * speedMultiplier * microVariation
      } else if (currentProgress < 60) {
        // Optimal transfer phase - fastest
        increment = (0.6 + Math.random() * 0.9) * speedMultiplier * microVariation
      } else if (currentProgress < 80) {
        // Congestion phase - slowing down
        increment = (0.3 + Math.random() * 0.5) * speedMultiplier * microVariation
      } else if (currentProgress < 90) {
        // Final chunks - very slow
        increment = (0.15 + Math.random() * 0.25) * speedMultiplier * microVariation
      } else if (currentProgress < 95) {
        // Last bits - crawling
        increment = (0.05 + Math.random() * 0.1) * speedMultiplier * microVariation
      } else {
        // Stuck at 95% waiting for real completion
        increment = 0.01
      }
      
      // Occasional small pauses to simulate network hiccups
      if (Math.random() < 0.02 && currentProgress > 10 && currentProgress < 90) {
        increment = 0 // 2% chance of brief pause
      }
      
      currentProgress = Math.min(currentProgress + increment, 95) // Never exceed 95% in simulation
      this.simulatedProgress.set(fileId, currentProgress)
      
      // Update UI with simulated progress
      const now = Date.now()
      if (now - lastUpdateTime > 100) { // Throttle UI updates to every 100ms
        this.updateProgressUI(fileId, Math.floor(currentProgress), 'simulating')
        lastUpdateTime = now
      }
      
      // Stop simulation at 95% and wait for real upload completion
      if (currentProgress >= 95) {
        this.updateProgressUI(fileId, 95, 'simulating')
        clearInterval(interval)
        this.progressIntervals.delete(fileId)
      }
    }, intervalDuration)
    
    this.progressIntervals.set(fileId, interval)
  }
  
  stopSimulatedProgress(fileId) {
    const interval = this.progressIntervals.get(fileId)
    if (interval) {
      clearInterval(interval)
      this.progressIntervals.delete(fileId)
    }
  }
  
  // Simple progress update
  updateProgressUI(fileId, progress, phase = 'upload') {
    const progressBar = document.querySelector(`[data-progress-bar="${fileId}"]`)
    const progressText = document.querySelector(`[data-progress-text="${fileId}"]`)
    const statusIcon = document.querySelector(`[data-status-icon="${fileId}"]`)
    const statusText = document.querySelector(`[data-status-text="${fileId}"]`)
    
    // Update progress bar
    if (progressBar) {
      progressBar.style.width = `${progress}%`
      
      // Simple color change based on phase
      if (phase === 'upload_complete') {
        progressBar.style.background = 'linear-gradient(to right, #10b981, #059669)'
      } else {
        progressBar.style.background = 'linear-gradient(to right, #3b82f6, #2563eb)'
      }
    }
    
    // Update text
    if (progressText) {
      progressText.textContent = `${progress}%`
      
      // Color based on completion
      if (progress >= 100) {
        progressText.classList.add('text-green-500')
        progressText.classList.remove('text-content')
      }
    }
    
    // Update status text
    if (statusText) {
      statusText.textContent = this.getStatusMessage(progress, phase)
    }
    
    // Update icon - simple states only
    if (statusIcon) {
      if (progress >= 100 && phase === 'upload_complete') {
        statusIcon.innerHTML = '<i class="fa-solid fa-check-circle text-green-500"></i>'
      } else {
        statusIcon.innerHTML = '<i class="fa-solid fa-cloud-arrow-up text-primary"></i>'
      }
    }
  }
  
  getEnhancedProgressEmoji(progress, phase) {
    if (progress >= 100) return '✅'
    
    if (phase === 'simulating') {
      // More engaging animated sequence during simulation
      const sequences = [
        ['🎵', '🎧', '🎶'], // Musical notes for audio analysis
        ['🔒', '☁️', '📡'], // Security and cloud connection
        ['⚙️', '🔧', '✨'], // Optimization tools
        ['🚀', '⚡', '💫'], // High-speed upload
        ['🎆', '✨', '✅'] // Finalization sparkles
      ]
      
      const phaseIndex = Math.min(Math.floor(progress / 20), sequences.length - 1)
      const emojiIndex = Math.floor(Date.now() / 800) % sequences[phaseIndex].length
      return sequences[phaseIndex][emojiIndex]
    }
    
    // Real upload progress emojis
    if (progress >= 90) return '🎆'
    if (progress >= 75) return '🚀'
    if (progress >= 50) return '⚡'
    if (progress >= 25) return '📡'
    return '🎵'
  }
  
  getStatusMessage(progress, phase) {
    if (progress >= 100) return 'Terminé'
    
    if (phase === 'simulating' || phase === 'upload') {
      // Messages simples basés sur la progression
      if (progress < 20) {
        return 'Préparation...'
      } else if (progress < 50) {
        return 'Upload en cours...'
      } else if (progress < 80) {
        return 'Traitement...'
      } else if (progress < 95) {
        return 'Finalisation...'
      } else {
        return 'Presque terminé...'
      }
    }
    
    return 'Upload en cours...'
  }
  
  // Simple progress update for upload only
  updateProgress(fileId, progress, phase = 'upload') {
    // Stop simulation if we're getting real progress
    if (progress > 5 && this.progressIntervals.has(fileId)) {
      this.stopSimulatedProgress(fileId)
    }
    
    // Stop simulation when real progress starts
    if (phase === 'upload' && progress > 0) {
      this.stopSimulatedProgress(fileId)
      // Ensure we don't go backwards from simulation
      const simulatedProgress = this.simulatedProgress.get(fileId) || 0
      progress = Math.max(progress, simulatedProgress)
    }
    
    // Use the enhanced UI update method
    this.updateProgressUI(fileId, progress, phase)
    
    // Mark complete at 100%
    if (phase === 'upload_complete' && progress >= 100) {
      this.markFileComplete(fileId)
    }
  }
  
  // Keep for backwards compatibility but redirect to updateProgress
  updateUnifiedProgress(fileId, progress, phase) {
    this.updateProgress(fileId, progress, phase)
  }
  
  markUploadComplete(fileId, signedBlobId) {
    // Called when upload is done - mark as 100%
    this.updateProgress(fileId, 100, 'upload_complete')
  }
  
  markUnifiedComplete(fileId) {
    this.completedUploads++
    
    // Update overall progress
    const totalFiles = this.uploadFiles.length
    this.overallProgressTarget.textContent = `${this.completedUploads}/${totalFiles}`
    
    // Check if all are complete
    if (this.completedUploads === totalFiles) {
      this.handleAllProcessingComplete()
    }
  }
  
  markFileFailed(fileId) {
    const statusIcon = document.querySelector(`[data-status-icon="${fileId}"]`)
    const progressBar = document.querySelector(`[data-progress-bar="${fileId}"]`)
    const statusText = document.querySelector(`[data-status-text="${fileId}"]`)
    
    if (statusIcon) statusIcon.innerHTML = '<i class="fa-solid fa-times-circle text-red-500"></i>'
    if (progressBar) progressBar.style.background = 'linear-gradient(to right, #ef4444, #dc2626)'
    if (statusText) statusText.textContent = 'Erreur lors du traitement'
    
    this.failedUploads++
  }
  
  handleAllProcessingComplete() {
    if (this.failedUploads === 0) {
      // All successful - enhanced success messaging
      this.headerTitleTarget.textContent = '🎉 Stems prêts !'
      this.headerSubtitleTarget.textContent = 'Upload et optimisation réussis avec succès'
      
      // Add some celebration flair
      this.headerIconTarget.innerHTML = '<i class="fa-solid fa-check-circle text-green-500 animate-bounce"></i>'
      
      // Show success screen with longer display for satisfaction
      setTimeout(() => {
        this.successScreenTarget.classList.remove('hidden')
        
        // Auto-hide after 3 seconds (longer for satisfaction)
        setTimeout(() => {
          this.hide()
        }, 3000)
      }, 800)
    } else {
      // Some failures - improved error messaging
      this.headerTitleTarget.textContent = '⚠️ Traitement partiellement réussi'
      this.headerSubtitleTarget.textContent = `${this.completedUploads} réussis, ${this.failedUploads} échoués`
      
      this.errorScreenTarget.classList.remove('hidden')
      this.errorMessageTarget.textContent = `${this.failedUploads} stem(s) n'ont pas pu être traités. Les autres sont prêts !`
    }
  }
}